import { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { useNls } from '../nls/nlsProvider'
import { AuthContext } from '../context/authContextCore'
import { WorkspaceContext } from '../context/workspaceContextCore'
import { isWorkspaceOwner } from '../utils/workspaceOwners'
import {
  isDmChannelData,
  canSendWithChatControl,
  type ChatControl,
  type MemberControls,
} from '../types/memberControls'
import { subscribeToMyEffectiveControls } from '../services/memberControlsService'

export interface UseChatControlsOptions {
  workspaceId?: string | null
  channel?: Record<string, unknown> | null
  effectiveControls?: MemberControls | null
  isOwner?: boolean
}

export interface UseChatControlsReturn {
  mode: ChatControl
  noticeMessage: string | null
  canSend: boolean
  canEditOwn: boolean
  canCreateChannel: boolean
  canCreateDm: boolean
  mentionFilter: (member: { uid?: string; id?: string } | string) => boolean
}

export function useChatControls(options?: UseChatControlsOptions): UseChatControlsReturn {
  const { t } = useNls()
  const auth = useContext(AuthContext)
  const workspaceContext = useContext(WorkspaceContext)

  const currentUser = auth?.currentUser
  const activeWorkspaceId = options?.workspaceId !== undefined
    ? options.workspaceId
    : (workspaceContext?.activeWorkspaceId ?? null)

  const activeWorkspace = workspaceContext?.workspaces?.find(
    (w) => w.id === activeWorkspaceId,
  )

  const derivedIsOwner = options?.isOwner !== undefined
    ? options.isOwner
    : Boolean(
        workspaceContext?.activeRole === 'OWNER' ||
        (activeWorkspace && currentUser?.uid && isWorkspaceOwner(activeWorkspace, currentUser.uid)),
      )

  const [subscribedControls, setSubscribedControls] = useState<MemberControls | null>(null)

  useEffect(() => {
    if (options?.effectiveControls !== undefined) return
    if (!activeWorkspaceId) return
    return subscribeToMyEffectiveControls(activeWorkspaceId, setSubscribedControls)
  }, [activeWorkspaceId, options?.effectiveControls])

  const controls = options?.effectiveControls !== undefined
    ? options.effectiveControls
    : subscribedControls

  const channel = options?.channel

  const mode: ChatControl = derivedIsOwner ? 'FULL' : (controls?.chat ?? 'FULL')

  let noticeMessage: string | null = null
  if (mode === 'OFF') {
    noticeMessage = t('NLSControlNoticeChatOff')
  } else if (mode === 'ANNOUNCEMENTS_ONLY') {
    const isAnnouncement = Boolean(channel && typeof channel === 'object' && channel.announcement === true)
    if (!isAnnouncement) {
      noticeMessage = t('NLSControlNoticeChatAnnouncementsOnly')
    }
  } else if (mode === 'NO_DM') {
    if (isDmChannelData(channel)) {
      noticeMessage = t('NLSControlNoticeChatNoDm')
    }
  }

  const canSend = derivedIsOwner ? true : canSendWithChatControl(mode, channel)
  const canEditOwn = derivedIsOwner ? true : (mode !== 'OFF' && mode !== 'ANNOUNCEMENTS_ONLY')
  const canCreateChannel = derivedIsOwner ? true : (mode !== 'OFF')
  const canCreateDm = derivedIsOwner ? true : (mode !== 'OFF' && mode !== 'NO_DM')

  const mentionFilter = useCallback((member: { uid?: string; id?: string } | string) => {
    if (derivedIsOwner || mode !== 'NO_DM') return true
    if (!channel || channel.type !== 'private') return true
    const memberUid = typeof member === 'string' ? member : (member.uid ?? member.id)
    if (!memberUid) return false
    const roles = channel.roles
    if (!roles || typeof roles !== 'object' || Array.isArray(roles)) return false
    return memberUid in roles
  }, [derivedIsOwner, mode, channel])

  return useMemo(() => ({
    mode,
    noticeMessage,
    canSend,
    canEditOwn,
    canCreateChannel,
    canCreateDm,
    mentionFilter,
  }), [mode, noticeMessage, canSend, canEditOwn, canCreateChannel, canCreateDm, mentionFilter])
}
